import React, { useState, useEffect, useMemo } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { 
  FaEye, FaTrashAlt, FaSearch, FaTimes, FaUser, 
  FaShippingFast, FaCreditCard, FaBoxOpen, FaCalendarAlt, FaCheckCircle 
} from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';

const CompletedOrder: React.FC = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  
  const [searchIdOrName, setSearchIdOrName] = useState<string>(''); 
  const [filterPayment, setFilterPayment] = useState<string>('All'); 
  const [filterDate, setFilterDate] = useState<string>(''); 
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);

  const fetchCompletedOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get('https://ayushvatika.com/api/orders/all', {
      // const res = await axios.get('http://localhost:8484/api/orders/all', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const deliveredOnly = res.data.filter((order: any) => order.status === 'DELIVERED');
      setOrders(deliveredOnly);
    } catch (err: any) {
      toast.error(err.response?.data || "Completed orders load nahi ho paye!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompletedOrders();
  }, []);

  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      const customerFullName = `${order.user.firstName} ${order.user.lastName}`.toLowerCase();
      const matchesSearch = order.id.toLowerCase().includes(searchIdOrName.toLowerCase()) || 
                            customerFullName.includes(searchIdOrName.toLowerCase());
      const matchesPayment = filterPayment === 'All' || order.paymentMode === filterPayment || order.paymentStatus === filterPayment;
      const orderDateOnly = new Date(order.orderDate).toISOString().split('T')[0];
      const matchesDate = !filterDate || orderDateOnly === filterDate;
      return matchesSearch && matchesPayment && matchesDate;
    });
  }, [orders, searchIdOrName, filterPayment, filterDate]);

  const handleDelete = async (id: string) => {
    if (!window.confirm("Kya aap is completed order ko delete karna chahte hain?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`https://ayushvatika.com/api/orders/${id}`, {
      // await axios.delete(`http://localhost:8484/api/orders/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success("Completed order deleted!");
      fetchCompletedOrders();
    } catch (err: any) {
      toast.error("Delete failed!");
    }
  };

  return (
    <AdminLayout>
      <div className="bg-gray-50 dark:bg-gray-900 p-6 min-h-screen transition-colors">
        <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-xl shadow-sm">
                <FaCheckCircle size={24}/>
            </div>
            <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">Completed Orders</h1>
        </div>
        
        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10 bg-green-50/50 dark:bg-green-900/10 p-6 rounded-2xl border border-green-100 dark:border-green-900/30">
            <div>
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block ml-1">Search ID / Name</label>
                <div className="relative">
                    <input
                      type="text"
                      placeholder="Find delivered order..."
                      className="w-full p-3 pl-10 border border-gray-300 dark:border-gray-600 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none shadow-sm
                      bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      value={searchIdOrName}
                      onChange={(e) => setSearchIdOrName(e.target.value)}
                    />
                    <FaSearch className="absolute left-3.5 top-4 text-gray-400" />
                </div>
            </div>
            <div>
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block ml-1">Payment Method</label>
                <select 
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-xl text-sm outline-none focus:ring-2 focus:ring-green-500 shadow-sm
                    bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    value={filterPayment}
                    onChange={(e) => setFilterPayment(e.target.value)}
                >
                    <option value="All">All Methods</option>
                    <option value="COD">COD (Cash on Delivery)</option>
                    <option value="PAID">PAID (Online)</option>
                </select>
            </div>
            <div>
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block ml-1">Delivered Date</label>
                <div className="relative">
                    <input 
                        type="date"
                        className="w-full p-3 pl-10 border border-gray-300 dark:border-gray-600 rounded-xl text-sm outline-none focus:ring-2 focus:ring-green-500 shadow-sm
                        bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        value={filterDate}
                        onChange={(e) => setFilterDate(e.target.value)}
                    />
                    <FaCalendarAlt className="absolute left-3.5 top-4 text-gray-400" />
                </div>
            </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700 font-black text-[11px] text-gray-500 dark:text-gray-300 uppercase tracking-tighter">
              <tr>
                <th className="px-6 py-5 text-left">Order ID</th>
                <th className="px-6 py-5 text-left">Customer</th>
                <th className="px-6 py-5 text-left">Completion Date</th>
                <th className="px-6 py-5 text-left">Amount</th>
                <th className="px-6 py-5 text-left">Payment Status</th>
                <th className="px-6 py-5 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
              {loading ? (
                <tr><td colSpan={6} className="text-center py-20 text-gray-400 italic">Fetching delivered orders...</td></tr>
              ) : filteredOrders.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-20 text-gray-400 font-bold text-lg italic">No completed orders found.</td></tr>
              ) : filteredOrders.map((order) => (
                <tr key={order.id} className="hover:bg-green-50/20 dark:hover:bg-green-900/10 transition-all group">
                  <td className="px-6 py-5 text-xs font-mono font-black text-green-800 dark:text-green-400 tracking-tight">{order.id}</td>
                  <td className="px-6 py-5">
                    <div className="text-sm font-black text-gray-900 dark:text-white">{order.user.firstName} {order.user.lastName}</div>
                    <div className="text-[10px] text-gray-400 font-medium">{order.user.email}</div>
                  </td>
                  <td className="px-6 py-5 text-xs text-gray-600 dark:text-gray-400 font-bold">
                    {new Date(order.orderDate).toLocaleDateString('en-GB')}
                  </td>
                  <td className="px-6 py-5 text-base font-black text-gray-900 dark:text-white">₹{order.totalAmount}</td>
                  <td className="px-6 py-5">
                    <span className="px-3 py-1.5 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-100 dark:border-green-800 rounded-xl text-[10px] font-black uppercase shadow-sm">
                      {order.paymentStatus || 'PAID'}
                    </span>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <div className="flex justify-center gap-4">
                        <button 
                            onClick={() => { setSelectedOrder(order); setShowModal(true); }} 
                            className="p-2.5 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-xl transition-all shadow-sm active:scale-90"
                        >
                            <FaEye size={18}/>
                        </button>
                        <button 
                            onClick={() => handleDelete(order.id)} 
                            className="p-2.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-xl transition-all shadow-sm active:scale-90"
                        >
                            <FaTrashAlt size={18}/>
                        </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal - Dark Compatible */}
      {showModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[100] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-[2rem] max-w-4xl w-full max-h-[92vh] overflow-y-auto shadow-2xl border-4 border-gray-50 dark:border-gray-700">
            
            <div className="sticky top-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm px-10 py-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center z-10">
              <div>
                <h2 className="text-3xl font-black text-gray-900 dark:text-white tracking-tighter">Completed Order Detail</h2>
                <p className="text-xs text-green-600 dark:text-green-400 font-mono mt-1 font-bold tracking-widest uppercase">ID: {selectedOrder.id}</p>
              </div>
              <button onClick={() => setShowModal(false)} className="p-3 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-gray-400 hover:text-red-500 transition-all duration-300">
                <FaTimes size={28} />
              </button>
            </div>

            <div className="p-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-12">
                  <div className="space-y-8">
                    <div>
                        <h3 className="text-xs font-black text-blue-600 dark:text-blue-400 uppercase tracking-[0.3em] flex items-center gap-3 mb-5">
                            <FaUser size={16}/> Customer Profile
                        </h3>
                        <div className="bg-gray-50 dark:bg-gray-700/50 p-6 rounded-3xl border border-gray-100 dark:border-gray-600 shadow-inner">
                            <p className="text-xl font-black text-gray-900 dark:text-white">{selectedOrder.user.firstName} {selectedOrder.user.lastName}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 font-semibold mt-1">{selectedOrder.user.email}</p>
                            <p className="text-sm text-gray-400 mt-0.5">{selectedOrder.user.mobile || 'No Contact'}</p>
                        </div>
                    </div>
                    <div>
                        <h3 className="text-xs font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-[0.3em] flex items-center gap-3 mb-5">
                            <FaShippingFast size={16}/> Delivery Location
                        </h3>
                        <div className="bg-gray-50 dark:bg-gray-700/50 p-6 rounded-3xl border border-gray-100 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300 leading-relaxed font-semibold shadow-inner italic">
                            {selectedOrder.shippingAddressSnapshot}
                        </div>
                    </div>
                  </div>

                  <div className="space-y-8">
                    <div>
                        <h3 className="text-xs font-black text-orange-600 dark:text-orange-400 uppercase tracking-[0.3em] flex items-center gap-3 mb-5">
                           <FaCreditCard size={16}/> Financial Summary
                        </h3>
                        <div className="bg-gray-50 dark:bg-gray-700/50 p-7 rounded-3xl border border-gray-100 dark:border-gray-600 shadow-inner space-y-6">
                            <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-600 pb-4">
                                <span className="text-sm text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest">Status</span>
                                <span className="px-4 py-1.5 rounded-2xl text-[10px] font-black uppercase shadow-sm bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-2 border-green-100 dark:border-green-800 flex items-center gap-1">
                                    <FaCheckCircle/> {selectedOrder.status}
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest">Payment</span>
                                <span className="text-base font-black text-gray-900 dark:text-white">{selectedOrder.paymentMode || 'COD'}</span>
                            </div>
                            <div className="flex justify-between items-end border-t-2 border-dashed border-gray-200 dark:border-gray-600 pt-6">
                                <span className="text-lg font-black text-gray-600 dark:text-gray-400">Net Amount</span>
                                <span className="text-4xl font-black text-blue-700 dark:text-blue-400 shadow-blue-500/10 shadow-lg">₹{selectedOrder.totalAmount}</span>
                            </div>
                        </div>
                    </div>
                  </div>
                </div>

                <div className="mt-12 pt-10 border-t-2 border-gray-50 dark:border-gray-700">
                    <h3 className="text-xs font-black text-gray-400 uppercase tracking-[0.4em] mb-8 flex items-center gap-3">
                        <FaBoxOpen size={18}/> Product Summary
                    </h3>
                    <div className="grid grid-cols-1 gap-5">
                        {selectedOrder.orderItems?.map((item: any, idx: number) => (
                          <div key={idx} className="flex items-center gap-6 bg-white dark:bg-gray-800 p-5 border-2 border-gray-50 dark:border-gray-700 rounded-[1.5rem] hover:border-green-200 dark:hover:border-green-800 transition-all duration-300">
                            <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-2xl border-2 border-gray-100 dark:border-gray-600 overflow-hidden flex-shrink-0">
                                <img 
                                    src={item.imageUrl || '/Logo/default-product.png'} 
                                    className="w-full h-full object-cover" 
                                    alt={item.productName} 
                                    onError={(e) => {e.currentTarget.src = '/Logo/default-product.png'}}
                                />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-lg font-black text-gray-900 dark:text-white truncate tracking-tight">{item.productName}</p>
                                <p className="text-sm text-gray-400 font-bold mt-1 uppercase">
                                    Qty: {item.quantity} <span className="mx-2 text-gray-200 dark:text-gray-600">|</span> Price: ₹{item.price}
                                </p>
                            </div>
                            <div className="text-2xl font-black text-gray-900 dark:text-white pr-4 italic">
                                ₹{item.quantity * item.price}
                            </div>
                          </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="px-10 py-8 bg-gray-50/80 dark:bg-gray-700/50 border-t border-gray-100 dark:border-gray-600 flex justify-end">
                <button onClick={() => setShowModal(false)} className="px-12 py-4 bg-gray-900 dark:bg-black text-white rounded-2xl text-xs font-black uppercase tracking-[0.2em] hover:bg-black dark:hover:bg-gray-900 transition-all shadow-lg">
                  Close Detailed View
                </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default CompletedOrder;